import React, { useEffect, useRef, useState } from 'react';
import { Segmented } from 'antd';

import { gridTierFor } from '../../utils/formGridCells';

/**
 * The widths the layout editor previews a form at: the screens a form is
 * actually read on. The width is the device's; how many columns that gives is
 * not decided here — the preview draws the real read view inside a frame of
 * that width, so the grid's own container query (css/components/form-grid.css)
 * picks the columns exactly as it will on the form. The caption names the
 * tier from the same constants a test holds to that stylesheet
 * (utils/formGridCells GRID_TIERS), measured on the grid it drew.
 */
export const PREVIEW_DEVICES = Object.freeze([
	Object.freeze({ key: 'desktop', label: 'Desktop', width: 1280 }),
	Object.freeze({ key: 'tablet', label: 'Tablet', width: 768 }),
	Object.freeze({ key: 'phone', label: 'Phone', width: 375 }),
]);

/** "12 columns", "6 columns", "one column". */
export const columnsLabel = columns => (columns === 1 ? 'one column' : `${columns} columns`);

/**
 * A frame the preview's children are drawn in at the chosen device's width,
 * shrunk to fit the room it has (`transform: scale`, which leaves the layout
 * width — and so the container query — alone). A device switch above it.
 *
 *   <FormLayoutPreview>{the read view}</FormLayoutPreview>
 *
 * Takes plain children, no layout state: the editor decides what to draw.
 * Without ResizeObserver (a test) the frame is not scaled and the caption
 * names the tier of the device's own width.
 */
export default function FormLayoutPreview({ children, initialDevice = 'desktop', className = '' }) {
	const [deviceKey, setDeviceKey] = useState(initialDevice);
	const device = PREVIEW_DEVICES.find(item => item.key === deviceKey) || PREVIEW_DEVICES[0];

	const viewportRef = useRef(null);
	const frameRef = useRef(null);
	const [room, setRoom] = useState(null);       // width the viewport has
	const [height, setHeight] = useState(null);   // the frame's own height, unscaled
	const [gridWidth, setGridWidth] = useState(null);

	useEffect(() => {
		if (typeof ResizeObserver === 'undefined') return undefined;
		const viewport = viewportRef.current;
		const frame = frameRef.current;
		if (!viewport || !frame) return undefined;
		const measure = () => {
			setRoom(viewport.clientWidth);
			setHeight(frame.offsetHeight);
			// The width the grid itself answers to: the first one on screen.
			const grid = [...frame.querySelectorAll('.form-grid')].find(node => node.clientWidth > 0);
			setGridWidth(grid ? grid.clientWidth : null);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(viewport);
		observer.observe(frame);
		return () => observer.disconnect();
	}, [deviceKey]);

	const scale = room ? Math.min(1, room / device.width) : 1;
	const tier = gridTierFor(gridWidth ?? device.width);

	return (
		<div className={`form-preview ${className}`.trim()}>
			<Segmented
				className="form-preview__devices"
				aria-label="Preview width"
				value={deviceKey}
				onChange={setDeviceKey}
				options={PREVIEW_DEVICES.map(item => ({ value: item.key, label: item.label }))}
			/>
			<p className="form-preview__caption" aria-live="polite">
				{device.label} · {device.width}px · {columnsLabel(tier.columns)}
				{scale < 1 ? ` · shown at ${Math.round(scale * 100)}%` : ''}
			</p>
			<div className="form-preview__viewport" ref={viewportRef} style={height ? { height: Math.ceil(height * scale) } : undefined}>
				<div
					className="form-preview__frame"
					ref={frameRef}
					data-device={device.key}
					data-tier={tier.key}
					style={{ width: device.width, transform: scale < 1 ? `scale(${scale})` : undefined }}
				>
					{children}
				</div>
			</div>
		</div>
	);
}
